import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from 'chart.js'

// Register Chart.js components
Chart.register(...registerables)

export default class extends Controller {
  static targets = [
    "performanceTab", "completionTab", "storiesTab", "errorsTab",
    "timelineChart", "resourceChart", "signalChart", "storyTimeChart",
    "phaseChart", "errorTypesChart", "exportFormat"
  ]

  connect() {
    this.buildSessionId = this.element.dataset.buildSessionId
    this.charts = {}
    this.currentTab = 'performance'
    
    // Initialize charts for the default tab
    this.initializePerformanceCharts()
  }

  disconnect() {
    // Clean up charts
    Object.values(this.charts).forEach(chart => {
      if (chart) chart.destroy()
    })
  }

  switchTab(event) {
    event.preventDefault()
    const tab = event.currentTarget.dataset.tab
    
    // Update tab buttons
    this.element.querySelectorAll('.tab').forEach(t => {
      t.classList.remove('tab-active')
    })
    event.currentTarget.classList.add('tab-active')
    
    // Hide all tabs
    this.element.querySelectorAll('.tab-content').forEach(content => {
      content.classList.add('hidden')
    })
    
    // Show selected tab and initialize charts if needed
    switch(tab) {
      case 'performance':
        this.performanceTabTarget.classList.remove('hidden')
        if (!this.charts.timeline) this.initializePerformanceCharts()
        break
      case 'completion':
        this.completionTabTarget.classList.remove('hidden')
        if (!this.charts.signal) this.initializeCompletionCharts()
        break
      case 'stories':
        this.storiesTabTarget.classList.remove('hidden')
        if (!this.charts.storyTime) this.initializeStoryCharts()
        break
      case 'errors':
        this.errorsTabTarget.classList.remove('hidden')
        if (!this.charts.errorTypes) this.initializeErrorCharts()
        break
    }
    
    this.currentTab = tab
  }

  initializePerformanceCharts() {
    // Execution Timeline Chart
    if (this.hasTimelineChartTarget) {
      const ctx = this.timelineChartTarget.getContext('2d')
      this.charts.timeline = new Chart(ctx, {
        type: 'line',
        data: {
          labels: this.generateTimeLabels(24),
          datasets: [{
            label: 'Stories Completed',
            data: this.generateMockData(24, 0, 5),
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            tension: 0.1
          }, {
            label: 'Errors',
            data: this.generateMockData(24, 0, 2),
            borderColor: 'rgb(239, 68, 68)',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            tension: 0.1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
            },
            title: {
              display: false
            }
          },
          scales: {
            y: {
              beginAtZero: true
            }
          }
        }
      })
    }
    
    // Resource Utilization Chart
    if (this.hasResourceChartTarget) {
      const ctx = this.resourceChartTarget.getContext('2d')
      this.charts.resource = new Chart(ctx, {
        type: 'line',
        data: {
          labels: this.generateTimeLabels(24),
          datasets: [{
            label: 'CPU Usage (%)',
            data: this.generateMockData(24, 20, 80),
            borderColor: 'rgb(34, 197, 94)',
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            tension: 0.1
          }, {
            label: 'Memory (MB)',
            data: this.generateMockData(24, 200, 800),
            borderColor: 'rgb(168, 85, 247)',
            backgroundColor: 'rgba(168, 85, 247, 0.1)',
            tension: 0.1,
            yAxisID: 'y1'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          plugins: {
            legend: {
              position: 'top',
            }
          },
          scales: {
            y: {
              type: 'linear',
              display: true,
              position: 'left',
              max: 100
            },
            y1: {
              type: 'linear',
              display: true,
              position: 'right',
              grid: {
                drawOnChartArea: false,
              }
            }
          }
        }
      })
    }
  }

  initializeCompletionCharts() {
    // Signal Contribution Chart
    if (this.hasSignalChartTarget) {
      const ctx = this.signalChartTarget.getContext('2d')
      this.charts.signal = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: ['Commands', 'Messages', 'Files', 'Tests', 'Explicit'],
          datasets: [{
            data: [30, 25, 20, 15, 10],
            backgroundColor: [
              'rgba(59, 130, 246, 0.8)',
              'rgba(34, 197, 94, 0.8)',
              'rgba(251, 191, 36, 0.8)',
              'rgba(168, 85, 247, 0.8)',
              'rgba(239, 68, 68, 0.8)'
            ]
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom',
            }
          }
        }
      })
    }
  }

  initializeStoryCharts() {
    // Story Time Distribution Chart
    if (this.hasStoryTimeChartTarget) {
      const ctx = this.storyTimeChartTarget.getContext('2d')
      this.charts.storyTime = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: ['0-5 min', '5-10 min', '10-15 min', '15-20 min', '20+ min'],
          datasets: [{
            label: 'Number of Stories',
            data: [5, 12, 8, 3, 2],
            backgroundColor: 'rgba(59, 130, 246, 0.8)'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            }
          },
          scales: {
            y: {
              beginAtZero: true
            }
          }
        }
      })
    }
    
    // Phase Performance Chart
    if (this.hasPhaseChartTarget) {
      const ctx = this.phaseChartTarget.getContext('2d')
      this.charts.phase = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: ['Foundation', 'Core Features', 'Enhanced Features', 'Polish'],
          datasets: [{
            label: 'Completed',
            data: [8, 12, 6, 2],
            backgroundColor: 'rgba(34, 197, 94, 0.8)'
          }, {
            label: 'Failed',
            data: [1, 2, 1, 0],
            backgroundColor: 'rgba(239, 68, 68, 0.8)'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              stacked: true,
            },
            y: {
              stacked: true,
              beginAtZero: true
            }
          }
        }
      })
    }
  }

  initializeErrorCharts() {
    // Error Types Chart
    if (this.hasErrorTypesChartTarget) {
      const ctx = this.errorTypesChartTarget.getContext('2d')
      this.charts.errorTypes = new Chart(ctx, {
        type: 'pie',
        data: {
          labels: ['Timeout', 'Command Failed', 'Network', 'Validation', 'Unknown'],
          datasets: [{
            data: [5, 8, 3, 2, 1],
            backgroundColor: [
              'rgba(239, 68, 68, 0.8)',
              'rgba(251, 191, 36, 0.8)',
              'rgba(59, 130, 246, 0.8)',
              'rgba(168, 85, 247, 0.8)',
              'rgba(107, 114, 128, 0.8)'
            ]
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'right',
            }
          }
        }
      })
    }
  }

  updateTimeRange(event) {
    const days = event.target.value
    
    if (days === 'custom') {
      // Show date picker modal
      this.showCustomDateRange()
    } else {
      // Fetch new data for the selected range
      this.fetchAnalyticsData(days)
    }
  }

  async refreshData(event) {
    event.preventDefault()
    
    // Add loading animation
    const button = event.currentTarget
    const icon = button.querySelector('i')
    icon.classList.add('fa-spin')
    
    try {
      await this.fetchAnalyticsData()
      this.updateAllCharts()
    } finally {
      icon.classList.remove('fa-spin')
    }
  }

  async fetchAnalyticsData(days = 7) {
    try {
      const response = await fetch(`/app_projects/${this.buildSessionId}/build_sessions/${this.buildSessionId}/automation_analytics?days=${days}`, {
        headers: {
          'Accept': 'application/json'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        this.updateChartsWithData(data)
      }
    } catch (error) {
      console.error('Error fetching analytics data:', error)
    }
  }

  updateChartsWithData(data) {
    // Update charts with real data
    // This would be implemented based on the actual data structure
  }

  updateAllCharts() {
    // Re-render all initialized charts
    Object.values(this.charts).forEach(chart => {
      if (chart) chart.update()
    })
  }

  viewStoryDetails(event) {
    event.preventDefault()
    const storyId = event.currentTarget.dataset.storyId
    
    // Open story details modal or navigate to story page
    window.location.href = `/app_projects/${this.buildSessionId}/user_stories/${storyId}`
  }

  viewErrorDetails(event) {
    event.preventDefault()
    const storyId = event.currentTarget.dataset.storyId
    
    // Show error details modal
    this.showErrorDetailsModal(storyId)
  }

  showErrorDetailsModal(storyId) {
    // Create and show error details modal
    const modal = document.createElement('dialog')
    modal.className = 'modal'
    modal.innerHTML = `
      <div class="modal-box">
        <h3 class="font-bold text-lg">Error Details for Story #${storyId}</h3>
        <div class="py-4">
          <div class="loading loading-spinner loading-md"></div>
          Loading error details...
        </div>
        <div class="modal-action">
          <form method="dialog">
            <button class="btn">Close</button>
          </form>
        </div>
      </div>
    `
    document.body.appendChild(modal)
    modal.showModal()
    
    // Fetch and display error details
    this.fetchErrorDetails(storyId, modal)
  }

  async fetchErrorDetails(storyId, modal) {
    // Fetch error details from server
    // Update modal content with details
  }

  exportReport(event) {
    event.preventDefault()
    document.getElementById('export-analytics-modal').showModal()
  }

  async performExport(event) {
    event.preventDefault()
    
    const format = this.exportFormatTarget.value
    const sections = this.getSelectedSections()
    
    try {
      const response = await fetch(`/app_projects/${this.buildSessionId}/build_sessions/${this.buildSessionId}/export_analytics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ format, sections })
      })
      
      if (response.ok) {
        const blob = await response.blob()
        this.downloadFile(blob, `analytics-report.${format}`)
        document.getElementById('export-analytics-modal').close()
      }
    } catch (error) {
      console.error('Error exporting report:', error)
      this.showNotification('Failed to export report', 'error')
    }
  }

  getSelectedSections() {
    const checkboxes = document.querySelectorAll('#export-analytics-modal input[type="checkbox"]:checked')
    return Array.from(checkboxes).map(cb => cb.value)
  }

  downloadFile(blob, filename) {
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }

  showNotification(message, type) {
    const toast = document.createElement('div')
    toast.className = `alert alert-${type} fixed bottom-4 right-4 z-50 max-w-sm`
    toast.innerHTML = `<span>${message}</span>`
    document.body.appendChild(toast)
    
    setTimeout(() => {
      toast.remove()
    }, 3000)
  }

  // Helper methods
  generateTimeLabels(hours) {
    const labels = []
    const now = new Date()
    for (let i = hours - 1; i >= 0; i--) {
      const time = new Date(now - i * 60 * 60 * 1000)
      labels.push(time.getHours() + ':00')
    }
    return labels
  }

  generateMockData(count, min, max) {
    const data = []
    for (let i = 0; i < count; i++) {
      data.push(Math.floor(Math.random() * (max - min + 1)) + min)
    }
    return data
  }
}